import { Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { PRIORITY, STATUS } from "@/lib/fleet";

export function Brand({ compact = false, className }) {
  return (
    <div className={cn("flex items-center gap-2.5", className)} data-testid="brand-logo">
      <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[0_8px_20px_-8px_rgba(234,179,8,0.7)]">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 3 L20 20 L12 15.5 L4 20 Z" fill="currentColor" stroke="none" />
        </svg>
      </div>
      {!compact && (
        <div className="leading-none">
          <div className="text-[15px] font-extrabold tracking-[0.12em]">GLAZY</div>
          <div className="mt-0.5 text-[9.5px] font-semibold tracking-[0.42em] text-muted-foreground">TRACKER</div>
        </div>
      )}
    </div>
  );
}

export function StatusBadge({ status, className, testId }) {
  const s = STATUS[status] || STATUS.offline;
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold", s.cls, className)}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.hex }} />
      {s.label}
    </span>
  );
}

export function PriorityBadge({ priority, className }) {
  const p = PRIORITY[priority] || PRIORITY.INFO;
  return <span className={cn("inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-bold tracking-wider", p.cls, className)}>{priority}</span>;
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between gt-rise">
      <div>
        {eyebrow && <div className="gt-eyebrow mb-2">{eyebrow}</div>}
        <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

const TONES = {
  default: "text-foreground bg-accent",
  yellow: "text-primary bg-primary/10",
  green: "text-emerald-500 bg-emerald-500/10",
  slate: "text-slate-400 bg-slate-500/10",
  blue: "text-blue-500 bg-blue-500/10",
  red: "text-red-500 bg-red-500/10",
  amber: "text-amber-500 bg-amber-500/10",
};

export function StatCard({ label, value, icon: Icon, tone = "default", hint, testId, active, onClick, delay = 0 }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} style={{ animationDelay: `${delay}ms` }}
      className={cn("gt-card gt-rise group min-w-[150px] p-4 text-left transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-primary/40",
        active && "border-primary/70")}>
      <div className="flex items-center justify-between">
        <span className="gt-eyebrow">{label}</span>
        <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg", TONES[tone])}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-3 font-mono text-3xl font-bold tracking-tight" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </button>
  );
}

export function NSelect({ value, onChange, children, testId, className }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className={cn("gt-input pr-8", className)}>
      {children}
    </select>
  );
}

export function Empty({ icon: Icon = Inbox, title, text, className }) {
  return (
    <div className={cn("flex flex-col items-center justify-center px-6 py-12 text-center", className)} data-testid="empty-state">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-muted-foreground"><Icon className="h-5 w-5" /></div>
      <div className="text-sm font-semibold">{title}</div>
      {text && <div className="mt-1 max-w-xs text-xs text-muted-foreground">{text}</div>}
    </div>
  );
}

export function Field({ label, children, className }) {
  return (
    <label className={cn("block", className)}>
      <span className="gt-eyebrow mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

export function KV({ label, value, mono = true, testId }) {
  return (
    <div className="rounded-xl border border-border/70 bg-background/40 px-3 py-2.5">
      <div className="gt-eyebrow text-[9.5px]">{label}</div>
      <div className={cn("mt-1 truncate text-[13px] font-semibold", mono && "font-mono")} data-testid={testId}>{value ?? "-"}</div>
    </div>
  );
}
